import React from 'react';
import { BatteryWarning } from 'lucide-react';
import { useLanguage } from '../../i18n';
import {
    GRAY_STACK_COLOR,
    energyBand,
    tankPercent,
    toPoints,
} from '../../utils/energyPalette';

// ─────────────────────────────────────────────────────────────────────────
// EnergyTank — the Hub's hero: the remaining figure over a SEGMENTED GAUGE.
//
//   [gray stack][=========== action ===========|        depleted        ]
//
// The GRAY stack sits far-left and never moves: it is the standing storage
// footprint, not something the user spends down. The ACTION segment to its
// right is the spendable remainder, coloured by band, and it SHRINKS across the
// cycle — that shrinking is the whole point, which is why the track's scale is
// the cycle's opening capacity (gray + opening action) rather than the current
// balance. Scaling to the balance would keep the bar permanently full and
// communicate nothing.
//
// The PAI design draws that track as 20 discrete segments rather than a
// continuous bar. The two zones stay PROPORTIONAL elements carrying inline
// percentage widths — that geometry is the tested contract (EnergyTank.test.jsx
// asserts `width: 50%` / `width: 0%` / `minWidth: 2px` on them) — and each zone
// simply renders its own share of the 20 segments inside itself. So the
// quantised gauge and the proportional zones are driven by ONE set of ratios and
// cannot drift apart.
//
// Purely presentational: it takes the tank and renders it. Fetching lives in
// useEnergyTank, which keeps this component trivially testable at the band
// boundaries.
// ─────────────────────────────────────────────────────────────────────────

const SEGMENTS = 20;

// The five health bands re-expressed in the PAI accent palette. `energyBand()`
// remains the single source of truth for WHICH band applies (and is what
// `data-energy-state` reports); this map only decides how that band is PAINTED,
// so a healthy tank reads in the design's own green while the degraded states
// keep their distinct signal.
const BAND_PAINT = {
    green: '#34D058',   // pai-green — "energy remaining"
    yellow: '#F5A524',  // pai-amber
    orange: '#F97316',
    red: '#F04438',     // pai-red
    empty: '#F04438',
};

// A zone's share of the 20 segments, from the same ratio that sets its width.
// A zone that is rendered at all keeps at least one segment, so a real-but-tiny
// footprint stays visible (the 2px width floor, in segment terms).
const segmentsFor = (widthPct) => (
    widthPct <= 0 ? 0 : Math.max(1, Math.round((widthPct / 100) * SEGMENTS))
);

const Segments = ({ count, color }) => (
    Array.from({ length: count }, (_, i) => (
        <span
            key={i}
            aria-hidden="true"
            className="flex-1 min-w-0 rounded-[5px]"
            style={{ background: color }}
        />
    ))
);

// `onClick` turns a legend entry into a real <button> — used by the Gray Stack,
// which drills into its own breakdown (#302). The value keeps its testid either
// way, so nothing that reads the figure cares whether it is clickable.
const Legend = ({ testId, label, value, swatch, onClick, title }) => {
    const Tag = onClick ? 'button' : 'div';
    return (
        <Tag
            {...(onClick ? { type: 'button', onClick, title } : {})}
            className={`flex items-center gap-1.5 min-w-0 text-left ${
                onClick ? 'rounded-chip px-1 -mx-1 hover:bg-white/[0.07] transition-colors' : ''
            }`}
        >
            {swatch && (
                <span
                    aria-hidden="true"
                    className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
                    style={{ background: swatch }}
                />
            )}
            <span className="text-[13px] text-pai-meta truncate">{label}</span>
            {/* The BE's 2dp string is rendered VERBATIM — never re-formatted. */}
            <span
                data-testid={testId}
                className="text-[13px] font-semibold text-white tnum flex-shrink-0"
            >
                {value}
            </span>
        </Tag>
    );
};

const EnergyTank = ({ tank, onOpenGrayStack }) => {
    const { t } = useLanguage();

    const gray = toPoints(tank?.gray_pts);
    const action = toPoints(tank?.action_pts);
    const opening = toPoints(tank?.cycle_opening_action_pts);

    const pct = tankPercent(tank);
    const { state, color } = energyBand(pct);
    const isEmpty = action <= 0;

    // max() because a mid-cycle top-up (cycle_deposit_pts) can legitimately push
    // the action side above what the cycle opened with; the bar must not
    // overflow its own track when it does.
    const scale = gray + Math.max(opening, action);
    let grayWidth;
    let actionWidth;
    if (isEmpty) {
        // Nothing spendable left — all that remains IS the gray stack.
        grayWidth = 100;
        actionWidth = 0;
    } else if (scale <= 0) {
        grayWidth = gray > 0 ? 100 : 0;
        actionWidth = 0;
    } else {
        grayWidth = (gray / scale) * 100;
        actionWidth = (action / scale) * 100;
    }

    // The gauge is quantised from the SAME ratios that drive the zone widths, so
    // the segment counts and the proportional geometry can never disagree.
    const graySegs = Math.min(SEGMENTS, segmentsFor(grayWidth));
    const actionSegs = Math.min(SEGMENTS - graySegs, segmentsFor(actionWidth));
    const emptySegs = Math.max(0, SEGMENTS - graySegs - actionSegs);
    const paint = BAND_PAINT[state] || color;

    const dash = '—';
    const cycle = [tank?.cycle_start, tank?.cycle_end]
        .filter(Boolean)
        .map((d) => String(d).slice(0, 10))
        .join(' → ');

    return (
        <section
            data-testid="energy-tank"
            className="pai-card px-6 py-5 min-w-0 max-w-full overflow-hidden"
        >
            <header className="flex items-center justify-between gap-2 flex-wrap min-w-0 mb-3">
                <h3 className="text-[17px] font-bold text-white truncate min-w-0">
                    {t('energyTankTitle', 'Energy Tank')}
                </h3>
                <div className="flex items-center gap-2 min-w-0">
                    {tank?.plan && (
                        <span
                            data-testid="energy-tank-plan"
                            className="text-[10.5px] font-bold uppercase tracking-[0.07em] px-2.5 py-1 rounded-chip border border-pai-strong text-pai-secondary flex-shrink-0"
                        >
                            {t(`plan_${tank.plan}`, tank.plan)}
                        </span>
                    )}
                    <span
                        data-testid="energy-tank-pct"
                        className="text-[14px] font-medium text-pai-meta tnum flex-shrink-0"
                    >
                        {`${Math.max(0, Math.round(pct))}%`}
                    </span>
                </div>
            </header>

            {/* The hero figure — the remaining spendable energy. The BE's 2dp
                string is printed VERBATIM and is the element's ONLY text (the
                suite anchors on /^-?\d+\.\d{2}$/), so the label and the opening
                capacity sit beside it rather than inside it. */}
            <div className="flex items-baseline gap-3 flex-wrap min-w-0 mb-3">
                <span
                    data-testid="energy-tank-action"
                    // An ABSENT tank is not an empty one: the placeholder stays
                    // achromatic rather than rendering a red em-dash.
                    className={`text-[44px] leading-none font-extrabold tracking-[-0.035em] tnum flex-shrink-0 ${tank ? '' : 'text-pai-faint'}`}
                    style={tank ? { color: paint } : undefined}
                >
                    {tank?.action_pts ?? dash}
                </span>
                <span className="text-[16px] font-medium text-pai-meta min-w-0 truncate">
                    {opening > 0 ? `/ ${tank.cycle_opening_action_pts}` : t('energyAction', 'Action points')}
                </span>
            </div>

            {/* The segmented gauge. Each ZONE keeps its proportional inline width
                — that geometry is the tested contract — and renders its own share
                of the 20 segments. */}
            <div
                className="flex h-7 w-full items-stretch gap-[5px]"
                role="img"
                aria-label={t('energyTankTitle', 'Energy Tank')}
            >
                {gray > 0 && (
                    // A <button> when the breakdown is reachable: the reservation
                    // is the one figure on this bar a user can actually ACT on
                    // (deleting files is how it shrinks), so it drills (#302).
                    <button
                        type="button"
                        data-testid="energy-gray-stack"
                        onClick={onOpenGrayStack}
                        disabled={!onOpenGrayStack}
                        aria-label={t('energyStorageBreakdown', 'Storage breakdown')}
                        title={
                            onOpenGrayStack
                                ? t('energyStorageBreakdown', 'Storage breakdown')
                                : t('energyGray', 'Gray points')
                        }
                        // A 2px floor so a tiny-but-real footprint is still VISIBLE.
                        // Purely cosmetic — it never feeds back into the numbers.
                        style={{ width: `${grayWidth}%`, minWidth: '2px' }}
                        className="flex items-stretch gap-[5px] min-w-0"
                    >
                        <Segments count={graySegs} color={GRAY_STACK_COLOR} />
                    </button>
                )}
                {/* Always rendered (even at 0 width) so the band state is
                    observable in every state, including 'empty'. */}
                <div
                    data-testid="energy-action-bar"
                    data-energy-state={state}
                    title={t('energyAction', 'Action points')}
                    style={{ width: `${actionWidth}%` }}
                    className="flex items-stretch gap-[5px] min-w-0 transition-all"
                >
                    <Segments count={actionSegs} color={paint} />
                </div>
                {emptySegs > 0 && (
                    <div aria-hidden="true" className="flex flex-1 items-stretch gap-[5px] min-w-0">
                        <Segments count={emptySegs} color="rgb(255 255 255 / 0.14)" />
                    </div>
                )}
            </div>

            {/* Axis. Zero on the left, the cycle's opening capacity on the right —
                the scale the gauge is actually drawn against. */}
            <div className="mt-2 flex items-center justify-between gap-2 text-[13px] text-pai-meta tnum min-w-0">
                <span>0</span>
                {opening > 0 && <span className="truncate">{tank.cycle_opening_action_pts}</span>}
            </div>

            {isEmpty && (
                <div
                    data-testid="energy-empty-badge"
                    className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-chip border border-pai-red/30 bg-pai-red/[0.1] text-pai-red text-[13px] font-medium max-w-full"
                >
                    <BatteryWarning size={14} className="flex-shrink-0" />
                    <span className="break-words min-w-0">{t('energyEmpty', "PAI's energy is empty.")}</span>
                </div>
            )}

            <div className="mt-4 pt-3 border-t border-pai-hairline flex flex-wrap items-center gap-x-5 gap-y-1.5 min-w-0">
                <Legend
                    testId="energy-tank-balance"
                    label={t('energyBalance', 'Balance')}
                    value={tank?.balance_pts ?? dash}
                />
                <Legend
                    testId="energy-tank-gray"
                    label={t('energyGray', 'Gray points')}
                    value={tank?.gray_pts ?? dash}
                    swatch={GRAY_STACK_COLOR}
                    onClick={onOpenGrayStack}
                    title={t('energyStorageBreakdown', 'Storage breakdown')}
                />
            </div>

            {cycle && (
                <div
                    data-testid="energy-tank-cycle"
                    className="mt-2 text-[12.5px] text-pai-faint break-words min-w-0 tnum"
                >
                    {`${t('energyCycle', 'Cycle')}: ${cycle}`}
                </div>
            )}
        </section>
    );
};

export default EnergyTank;
